import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Estado } from './estado';

export interface EstadosState {
  list: PaginatedList<Estado>;
  current: Estado;
  actives: Estado[];

  nuevo: () => void;
  cargarPaginados: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivos: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarEstado) => Promise<boolean>;
  actualizar: (id: number, request: ActualizarEstado) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarEstado {
  nombre: string;
}

export interface ActualizarEstado {
  nombre: string;
}

const { showSuccessToast } = useUIStore.getState();

export const useEstadosStore = create<EstadosState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as Estado,
  actives: [],

  nuevo: () =>
    set({
      current: {} as Estado
    }),
  cargarPaginados: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<Estado>>(
      `/estados/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivos: async () => {
    const data = await makeGet<Array<Estado>>('/estados/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<Estado>(`/estados/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarEstado) => {
    const data = await makePost<RegistrarEstado, Estado>('/estados/', request);

    if (data.isFailure) return false;

    showSuccessToast('Registro agregado');

    set({
      current: {} as Estado
    });

    return true;
  },
  actualizar: async (id: number, request: ActualizarEstado) => {
    const data = await makePatch<ActualizarEstado, Estado>(`/estados/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/estados/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activo: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/estados/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activo: false
      }
    });

    return true;
  }
}));
